import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { quoteDetailPageQueryOptions, type QuoteDetailPageSearch } from "~/app/api/pageData/quoteDetailPageQuery";
import type { QuoteDetailPageInitialData } from "~/app/api/pageData/quoteDetailPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  createQuoteDetailsPage,
  type QuoteDetailsPageProps,
} from "~/features/orders/quoteDetails/components/v2/QuoteDetailsPage";
import { parseDetailRouteSearch, toPageDataDetailSearch, type DetailRouteSearch } from "./detailRouteSearch";

const QUOTE_DETAIL_PATH = "/my-account/quotes/detail";

function QuoteDetailRoute() {
  const result = useLoaderData({
    from: QUOTE_DETAIL_PATH,
  }) as PageInitialData<QuoteDetailPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const pageProps = parsePageData(result.data?.pageDataJson);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {pageProps ? createQuoteDetailsPage(pageProps) : null}
    </StorefrontShellFrame>
  );
}

function parsePageData(pageDataJson: string | undefined): QuoteDetailsPageProps | null {
  if (!pageDataJson) {
    return null;
  }
  try {
    return JSON.parse(pageDataJson) as QuoteDetailsPageProps;
  } catch {
    return null;
  }
}

export function parseQuoteDetailSearch(search: Record<string, unknown>): DetailRouteSearch {
  return parseDetailRouteSearch(search);
}

export async function loadQuoteDetailPageData(context: { queryClient: QueryClient }, search: DetailRouteSearch = {}) {
  const pageDataSearch: QuoteDetailPageSearch = toPageDataDetailSearch(search);
  const result = await context.queryClient.ensureQueryData(quoteDetailPageQueryOptions(pageDataSearch));

  return unwrapPageInitialData(result);
}

export const quoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: QUOTE_DETAIL_PATH,
  validateSearch: parseQuoteDetailSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadQuoteDetailPageData(context, deps),
  component: QuoteDetailRoute,
});
